// The gate's client-side checks and request body. Pure, like store.ts. The Worker repeats
// every check (src/server/prompt-builder/capture.ts); these only save a round trip.

import { classifyEmailSync } from "../../shared/leadgen/work-email";
import {
  fieldErrorCopy,
  type CaptureField,
  type CaptureRequestBody,
} from "../../shared/prompts/capture-contract";
import { isAccountCountId, isRoleId } from "../../shared/prompts/gate-options";
import type { BuilderState } from "./store";

export function checkEmailField(email: string): { error: string | null; nudge: boolean } {
  if (email.trim() === "") return { error: fieldErrorCopy("email", "required"), nudge: false };
  const check = classifyEmailSync(email);
  if (!check.ok) {
    return {
      error: fieldErrorCopy("email", check.reason === "disposable" ? "disposable" : "invalid"),
      nudge: false,
    };
  }
  return { error: null, nudge: check.type === "freemail" };
}

export function checkGate(state: BuilderState): {
  fieldErrors: Partial<Record<CaptureField, string>>;
  nudge: boolean;
} {
  const fieldErrors: Partial<Record<CaptureField, string>> = {};
  const email = checkEmailField(state.gate.email);
  if (email.error) fieldErrors.email = email.error;
  if (!isRoleId(state.gate.role)) fieldErrors.role = fieldErrorCopy("role", "required");
  if (!state.inputs.businessType) {
    fieldErrors.businessType = fieldErrorCopy("businessType", "required");
  }
  if (!isAccountCountId(state.gate.accounts)) {
    fieldErrors.accounts = fieldErrorCopy("accounts", "required");
  }
  return { fieldErrors, nudge: email.nudge };
}

export function captureBody(
  state: BuilderState,
  turnstileToken: string | null,
): CaptureRequestBody {
  const { inputs, gate } = state;
  return {
    email: gate.email.trim(),
    role: gate.role,
    businessType: inputs.businessType,
    accounts: gate.accounts,
    newsletter: gate.newsletter,
    inputs: {
      goal: inputs.goal,
      platform: inputs.platform,
      brand: inputs.brand.trim(),
      audience: inputs.audience.trim(),
      tone: inputs.tone.trim(),
      pillars: inputs.pillars.trim(),
      promptId: inputs.promptId,
      slots: Object.fromEntries(
        Object.entries(inputs.slots).filter(
          (entry): entry is [string, string] => typeof entry[1] === "string",
        ),
      ),
    },
    ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  };
}
